// Core build: no session recorder (record_sessions_percent stays at its default 0).
import mixpanel from "mixpanel-browser/dist/mixpanel-core.cjs.js";
import * as chainSession from "@/modules/ChainSession";
import { readMcpAppProps } from "@/modules/mcpScope";

const mixpanelToken = process.env.MIXPANEL_TOKEN;
const mixpanelDebug = process.env.MIXPANEL_DEBUG === true;

let initialized = false;

/**
 * Player iframe only: init Mixpanel after experience payload is applied so tracking attaches to full session.
 *
 * E2E: when the player is loaded with `?pix_run_id=<id>`, register it as a Mixpanel
 * super-property so every event carries `properties.pix_run_id`. The backend E2E
 * analytics sync uses it to scope Mixpanel Raw Export results to a single Playwright
 * run. Absent in prod URLs → property simply isn't attached to events.
 *
 * MCP-App: when the player is loaded with `?pix_surface=<name>&pix_host=<host>`
 * (and, for a custom MCP, `pix_mcp=<slug>`), register them as Mixpanel
 * super-properties so every event carries the embedding context. Absent in
 * regular URLs → properties simply aren't attached to events.
 */
export function ensureMixpanelInitialized() {
  if (initialized || !mixpanelToken) {
    return;
  }
  initialized = true;
  mixpanel.init(mixpanelToken, {
    debug: mixpanelDebug,
    track_pageview: false,
    persistence: "localStorage",
  });
  mixpanel.identify(String(Date.now()));
  const pixRunId = readPixRunIdFromUrl();
  if (pixRunId) {
    mixpanel.register({ pix_run_id: pixRunId });
  }
  const mcpAppProps = readMcpAppProps();
  if (Object.keys(mcpAppProps).length > 0) {
    // Non-persistent: mcp-app tagging applies to this pageload only, so stale
    // embedding context never leaks into later organic top-level sessions.
    mixpanel.register(mcpAppProps, { persistent: false });
  }
  const utmProps = readUtmFromUrl();
  if (Object.keys(utmProps).length > 0) {
    // Non-persistent: utm applies to this pageload only, so stale campaign attribution never leaks into later organic sessions.
    mixpanel.register(utmProps, { persistent: false });
  }
  // Chain success-metric grouping. Session id + seed are stable for the whole
  // pageload; depth is the 1-based position of the current experience in the
  // chain (seed = 1) and is re-registered on each in-place advance. Registered
  // for every session so the seed's own events carry chain context; a session
  // "chains" only once depth exceeds 1 or a funnel event fires.
  const seedId =
    window.pixmoto &&
    window.pixmoto.experienceMetadata &&
    window.pixmoto.experienceMetadata.idexperience != null
      ? window.pixmoto.experienceMetadata.idexperience
      : null;
  mixpanel.register({
    pix_chain_session_id: chainSession.getSessionId(),
    pix_chain_seed_idexperience: seedId,
    pix_chain_depth: chainSession.depth() + 1,
  });
  window.mixpanel = mixpanel;
}

/**
 * Re-register the chain depth super-property so every subsequent event carries
 * the current 1-based chain position. No-ops until Mixpanel is initialized
 * (the init path already registers the starting depth).
 */
export function registerChainDepth(depth1Based: number) {
  if (!window.mixpanel || typeof window.mixpanel.register !== "function") {
    return;
  }
  window.mixpanel.register({ pix_chain_depth: depth1Based });
}

function readPixRunIdFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("pix_run_id") || null;
  } catch (_) {
    return null;
  }
}

/**
 * Capture campaign attribution for OpenAI Ads (and other) play-url traffic.
 * mixpanel-browser does not auto-register utm params as super-properties, so
 * read them from the URL and attach to every event. Absent params are skipped.
 */
function readUtmFromUrl(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
    ];
    for (let i = 0; i < keys.length; i++) {
      const val = params.get(keys[i]);
      if (val) {
        out[keys[i]] = val;
      }
    }
  } catch (_) {
    /* URLSearchParams unavailable — no attribution captured */
  }
  return out;
}
